import React, {useEffect, useState} from 'react';
import {ToastSeverityType} from "primereact/toast";
import {IConfigDataConfigLoadingConfig} from "../product-template-table/utils/interfaces";
import {DashboardInfos} from "../../../api/apiModels";
import {
    useCleanupReportHistoryRange,
    useDeleteReportRange,
    useGetDashboardsInfo
} from "../../../api/api-hooks/apiHooks";
import {getTemplateLoadingRange} from "../product-template-table/utils/timeUtils";
import {TemplateLoadingType} from "../product-template-table/utils/enums";
import {ProgressSpinner} from "primereact/progressspinner";
import {Dropdown} from "primereact/dropdown";
import {InputText} from "primereact/inputtext";
import CalendarWithDebounce from "../../basic/calendar-with-debounce/CalendarWithDebounce";
import {Button} from "primereact/button";

type RemoveReportRangeType = {
    ids: Array<number>
    showToast(severity: ToastSeverityType, summary: string, detail: string, lifetime?: number): void
    loadingConfig?: IConfigDataConfigLoadingConfig
    function: "REMOVE" | "CLEANUP"

    close(): void
}

const RemoveReportRange = (props: RemoveReportRangeType) => {
    const [dashboard, setDashboard] = useState<DashboardInfos | undefined>(undefined);

    const [fromDate, setFromDate] = useState<Date | undefined>();
    const [toDate, setToDate] = useState<Date | undefined>();

    const {data, isLoading} = useGetDashboardsInfo(props.ids);

    const {mutate: cleanupRange} = useCleanupReportHistoryRange({
        onSuccess: () => {
            props.showToast("success", "", "Successfully cleaned up report history range.")
        },
        onFailure: () => {
            props.showToast("error", "", "An error occurred while cleaning up report history range.")
        },
        onMutate: () => {
            props.showToast("info", "", "Initiating cleanup of report history range.")
            props.close()
        }
    })

    const {mutate: removeRange} = useDeleteReportRange({
        onSuccess: () => {
            props.showToast("success", "", "Successfully removed report range.")
        },
        onFailure: () => {
            props.showToast("error", "", "An error occurred while removing report range.")
        },
        onMutate: () => {
            props.showToast("info", "", "Initiating removal of report range.")
            props.close()
        }
    })

    useEffect(() => {
        let {start, end} = getTemplateLoadingRange({
            loadingType: TemplateLoadingType.RANGE,
            loadingConfig: props.loadingConfig
        });

        setFromDate(start || new Date());
        setToDate(end || new Date());
    }, [])

    useEffect(() => {
        if (data && !isLoading && data.length === 1) {
            setDashboard(data[0])
        }
    }, [data, isLoading])

    const onDashboardChange = (e: { value: any }) => {
        setDashboard(e.value);
    }

    const onButtonClick = () => {
        if (props.function === "REMOVE") {
            onRemoveRangeClick()
        } else if (props.function === "CLEANUP") {
            onCleanupHistoryRangeClick()
        }
    }

    const onRemoveRangeClick = () => {
        if (!dashboard || !fromDate || !toDate) {
            props.showToast("warn", "Warning", "No dashboard selected")
            return
        }

        removeRange({dashboardId: dashboard.id, from: fromDate.toISOString(), to: toDate.toISOString()})
        props.close()
    }

    const onCleanupHistoryRangeClick = () => {
        if (!dashboard || !fromDate || !toDate) {
            props.showToast("warn", "Warning", "No dashboard selected")
            return
        }

        cleanupRange({dashboardId: dashboard.id, params: {from: fromDate.toISOString(), to: toDate.toISOString()}})
        props.close()
    }

    if (isLoading) {
        return <ProgressSpinner/>
    }

    return (
        <div className={"flex flex-column gap-3"}>
            <div>
                {props.function === "REMOVE" ?
                    <>Permanently erase all report generations from <br/> the dashboard.</>
                    :
                    <>Deletes the dashboard report generation history<br/> preserves only the latest report.</>
                }
            </div>

            <div className={"flex flex-column"}>
                <label htmlFor="report"><b>Dashboard</b></label>
                {props.ids.length > 1 ?
                    <Dropdown value={dashboard} options={data} onChange={onDashboardChange}
                              optionLabel="title"
                              placeholder="Select a Dashboard"/>
                    :
                    <>
                        {
                            props.ids.length === 1 &&
                            <InputText value={dashboard?.title || ""} disabled/>
                        }
                    </>
                }
            </div>

            <div className={"flex flex-column gap-1"}>
                <div style={{fontWeight: "bold", fontSize: "larger", paddingTop: "5px"}}>Reference Time</div>
                <div className={"flex flex-column"}>
                    <label htmlFor="from"><b>From</b></label>
                    <CalendarWithDebounce id="from" value={fromDate || new Date()} onChange={setFromDate}/>
                </div>
                <div className={"flex flex-column"}>
                    <label htmlFor="to"><b>To</b></label>
                    <CalendarWithDebounce id="to" value={toDate || new Date()} onChange={setToDate}/>
                </div>
            </div>

            <Button label={"Proceed"} onClick={() => onButtonClick()} disabled={!dashboard}/>
        </div>
    );
};

export default RemoveReportRange;